import { assert } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import { openUrl } from '@disy/lib/open-url';
import { uniqueId } from '@disy/lib/unique-id';
import type { Column, ColumnKey } from '@disy/ui.responsive-table/responsive-table';
import { ACTIONS_COLUMN_KEY, TableEvents } from '@disy/ui.responsive-table/responsive-table-constants';
import { iconButtonSize } from '@disy/ui/button';
import type { Icon } from '@disy/ui/icon';
import type { ToolbarContent, ToolbarItem } from '@disy/ui/toolbar';
import { Toolbar, createSmallLinkIconButton } from '@disy/ui/toolbar';

import './responsive-table-actions.css';
import i18n from './responsive-table-actions.properties';

export interface TableAction<T> {
  /** Label for the action */
  label: string;
  /** Icon displayed in the table */
  icon: Icon;
  /** Onclick handler*/
  execute?: (event: MouseEvent, rowData: T, rowIndex: number) => void;
  /** Provider function for a link's url */
  url?: (rowData: T) => string;
  /** Link target in case a url provider function is used. For possible values see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target */
  target?: string;
  /** There is at most one main action per table row. It is triggered when pressing enter on the row. */
  isMainAction?: boolean;
  /** Checks whether the action should be displayed. If it is not provided action is displayed. */
  available?: (rowData: T, rowIndex: number) => boolean;
  /** Checks whether the action should be enabled. If false the corresponding action is greyed out. Defaults to true. */
  enabled?: (rowData: T, rowIndex: number) => boolean;
  /** The content of a sub-menu */
  subMenu?: (rowData: T, tr: HTMLElement, rowIndex: number) => ToolbarItem['subMenu'];
  responsiveBehavior?: ToolbarItem['responsiveBehavior'];
}

export interface ActionsProvider<T> {
  (options: { rowData: T; rowIndex: number }): (TableAction<T> | HTMLElement)[];
}

/**
 * Creates a table column with action controls.
 *
 * @param actions - Array of action descriptors
 *   (The number of actions defines the width of the column.)
 * @return The action column
 */
export function createActionsColumn<T>(actions: TableAction<T>[]): Column<T>;
/**
 * Creates a table column with action controls.
 *
 * @param maxNumberOfActions - The maximum number of actions
 *   (Some actions might not be available in some rows, but we need
 *   the maximum number to define the width of the column.)
 * @param actionsProvider - A function returning an array of action descriptors
 * @return The action column
 */
export function createActionsColumn<T>(maxNumberOfActions: number, actionsProvider: ActionsProvider<T>): Column<T>;
export function createActionsColumn<T>(
  actionsOrNumber: TableAction<T>[] | number,
  actionsProvider?: ActionsProvider<T>,
): Column<T> {
  return {
    name: '',
    key: ACTIONS_COLUMN_KEY as ColumnKey<T>,
    width: computeActionsColumnWidth(Array.isArray(actionsOrNumber) ? actionsOrNumber.length : actionsOrNumber),
    renderer: createActionsRenderer(Array.isArray(actionsOrNumber) ? () => actionsOrNumber : actionsProvider!),
  };
}

function computeActionsColumnWidth(maxNumberOfActions: number) {
  const horizontalSpacingInPx = 4;
  const toolbarPadding = 3;
  return `${maxNumberOfActions * iconButtonSize('s') + (maxNumberOfActions - 1) * horizontalSpacingInPx + 1 + toolbarPadding * 2}px`;
}

function createActionsRenderer<T>(actionsProvider: ActionsProvider<T>) {
  return (
    data: unknown,
    { rowData, rowIndex, table, tr }: { rowData: T; tr: HTMLElement; table: HTMLElement; rowIndex: number },
  ) => {
    const actions = actionsProvider({ rowData, rowIndex });
    assert(
      actions.filter((action) => !(action instanceof HTMLElement) && action.isMainAction === true).length <= 1,
      'There can be only one main action.',
    );

    const toolbarItems = actions
      .filter((action) => action instanceof HTMLElement || (action.available?.(rowData, rowIndex) ?? true)) // do not render actions when they are not supported
      .map((action) =>
        action instanceof HTMLElement ? action : (
          {
            label: action.label,
            icon: action.icon.cloneNode(true) as Icon,
            execute: action.execute && ((event: MouseEvent) => action.execute!(event, rowData, rowIndex)),
            url: action.url?.(rowData),
            target: action.target,
            isMainAction: action.isMainAction,
            subMenu: action.subMenu?.(rowData, tr, rowIndex),
            disabled: action.enabled?.(rowData, rowIndex) === false,
          }
        ),
      );

    setupKeyboardEventListenerForMainAction(toolbarItems, tr);
    return createActionsToolbar(toolbarItems, tr, table);
  };
}

function setupKeyboardEventListenerForMainAction(
  toolbarItems: ((ToolbarItem & { isMainAction?: boolean }) | HTMLElement)[],
  tr: HTMLElement,
) {
  const mainAction = toolbarItems.find(
    (item) => !(item instanceof HTMLElement) && item.isMainAction === true,
  ) as ToolbarItem;
  if (!mainAction) {
    return;
  }
  on(tr, 'keydown', (event) => {
    if (event.target === tr && event instanceof KeyboardEvent && event.key === 'Enter') {
      if (mainAction.url) {
        openUrl(mainAction.url, { target: mainAction.target ?? '_self' }); // by default open the url in the current browsing context
      } else if (mainAction.execute) {
        mainAction.execute(new MouseEvent('click'));
      }
    }
  });
}

function createActionsToolbar(toolbarContent: ToolbarContent, tr: HTMLElement, table: HTMLElement) {
  const toolbar = new Toolbar(toolbarContent, { defaultItemRenderer: createSmallLinkIconButton });
  toolbar.classList.add('d-responsive-table--actions-toolbar');
  setupAccessibilityLabelOnTableRefresh(toolbar, tr, table);
  return toolbar;
}

// The accessibility name of the toolbar can set only after the table is completely rendered since it depends on the content
function setupAccessibilityLabelOnTableRefresh(toolbar: Toolbar, tr: HTMLElement, table: HTMLElement) {
  toolbar.id = uniqueId();
  toolbar.ariaLabel = i18n('aria.toolbar');
  const target = table.closest('d-responsive-table');
  if (target) {
    on(target, TableEvents.REFRESH, () => {
      // The actions toolbar is labelled by the main cell of the row and its own aria-label
      toolbar.setAttribute('aria-labelledby', `${tr.querySelector('th[scope=row]')?.id} ${toolbar.id}`);
    });
  }
}
